import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import sumBy from 'lodash/sumBy';
import {
    CHALLENGES_REQUESTED,
    TAGS_REQUESTED,
    USERS_REQUESTED,
    REPORT_PREVIEW_REQUESTED,
    REPORT_CSV_REQUESTED,
    COUNTRIES_AND_CITIES_REQUESTED,
    REPORT_MAP_DATA_REQUESTED,
} from './constants';
import {
    challengesSucceeded,
    challengesFailed,
    tagsSucceeded,
    usersSucceeded,
    tagsFailed,
    usersFailed,
    generatePreviewReportSucceeded,
    generatePreviewReportFailed,
    generateCSVReportSucceeded,
    generateCSVReportFailed,
    countriesAndCitiesFailed,
    countriesAndCitiesSucceeded,
    setMapDataFailed,
    setMapDataSuccess,
} from './actions';
import {
    getChallengeNames,
    getTags,
    getUsers,
    getReport,
    getPartnerCountries,
    getLitters,
} from '../../api';
import { parseClientError } from '../../api/utils';
// import { getTotalCount } from './selectors';
import {
    arrayOfObjectsToIdsStr,
    arrayOfObjectsToTitleStr,
} from '../../common/utils';
import { impactFilterSucceeded } from '../../map/actions';
import { getUserData } from '../auth/selectors';
import { logException } from '../../logger';

function* fetchPartnerCountries() {
    try {
        const countries = yield call(getPartnerCountries);
        yield put(countriesAndCitiesSucceeded(countries.data));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(countriesAndCitiesFailed(message));
    }
}

// triggers when user `CHALLENGES_REQUESTED` action is dispatched
function* fetchChallenges() {
    try {
        const challenges = yield call(getChallengeNames);
        yield put(challengesSucceeded(challenges));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(challengesFailed(message));
    }
}

// triggers when user `TAGS_REQUESTED` action is dispatched
function* fetchTags() {
    try {
        const tags = yield call(getTags);
        yield put(tagsSucceeded(tags));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(tagsFailed(message));
    }
}

// triggers when user `USERS_REQUESTED` action is dispatched
function* fetchUsers() {
    try {
        const users = yield call(getUsers);
        yield put(usersSucceeded(users));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(usersFailed(message));
    }
}

// triggers when user `REPORT_REQUESTED` action is dispatched
function* fetchReport(action) {
    try {
        const params = {
            challengeIds: arrayOfObjectsToIdsStr(action.selectedChallenges),
            tags: arrayOfObjectsToTitleStr(action.selectedTags),
            userIds: arrayOfObjectsToIdsStr(action.selectedUsers),
            countryCodes: arrayOfObjectsToIdsStr(action.selectedCountries),
            startDate: action.selectedStartDate
                ? new Date(action.selectedStartDate)
                : '',
            endDate: action.selectedEndDate
                ? new Date(action.selectedEndDate)
                : '',
            location: action.selectedZipcode,
            radius: action.selectedRadius,
        };
        if (action.tagSource) {
            params.source =
                action.tagSource == 'litterati' ? 'litterati' : 'user';
        }
        if (params.startDate) {
            params.startDate = moment(params.startDate).format('yyyy-MM-DD');
        }
        if (params.endDate) {
            params.endDate = moment(params.endDate).format('yyyy-MM-DD');
        }

        let offset = 0;
        const limit = 5;

        // make first api call in order to get total count
        // and render a preview to the user
        // so we can concurrently make the rest of the api calls
        const res = yield call(getReport, {
            ...params,
            offset,
            limit,
        });

        const metaTotal = res.meta ? res.meta.total : 0;
        let result = res.data ? res.data : [];

        yield put(
            generatePreviewReportSucceeded({
                previewReport: result,
                totalCount: metaTotal,
            }),
        );
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(generatePreviewReportFailed(message));
    }
}

function* fetchCSVData(action) {
    try {
        const params = {
            challengeIds: arrayOfObjectsToIdsStr(action.selectedChallenges),
            userIds: arrayOfObjectsToIdsStr(action.selectedUsers),
            countryCodes: arrayOfObjectsToIdsStr(action.selectedCountries),
            city: arrayOfObjectsToIdsStr(action.selectedCities),
            startDate: action.selectedStartDate
                ? new Date(action.selectedStartDate)
                : '',
            endDate: action.selectedEndDate
                ? new Date(action.selectedEndDate)
                : '',
            location: action.selectedZipcode,
            radius: action.selectedRadius,
            tags: action.selectedTags.map((tag) => tag.title).join(','),
        };
        let source = 'litterati';
        if (action.tagSource) {
            source =
                action.tagSource.toLowerCase() == 'litterati'
                    ? 'litterati'
                    : 'user';
        }

        if (action.tagSource) {
            params.source = source;
        }
        if (params.startDate) {
            params.startDate = moment(params.startDate).format('yyyy-MM-DD');
        }
        if (params.endDate) {
            params.endDate = moment(params.endDate).format('yyyy-MM-DD');
        }

        let callStack = [];
        const reportResponse = yield call(getReport, {
            ...params,
        });
        const totalCount =
            reportResponse.meta.total ||
            sumBy(reportResponse.data, (item) => parseInt(item.litterCount));
        callStack.push(reportResponse);

        const report = callStack.reduce((result, res) => {
            return res.data ? result.concat(res.data) : null;
        }, []);

        yield put(
            impactFilterSucceeded({
                impactLitters: report,
                hasMoreData: false,
            }),
        );

        yield put(
            generateCSVReportSucceeded({
                report,
                totalCount: totalCount,
            }),
        );
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(generateCSVReportFailed(message));
    }
}

function* filterReportLitters(action) {
    let dimension = {
        ...(action.dimensions || {
            top: 90,
            bottom: -90,
            left: -180,
            right: 180,
        }),
    };
    if (dimension.right < 0) dimension.right = Math.abs(dimension.right);
    if (dimension.left > 0) dimension.left = -dimension.left;

    // if (dimension.top > 90) dimension.top = 90;
    // if (dimension.bottom > -90) dimension.bottom = -90;
    // if (dimension.left > -180) dimension.left = -180;
    // if (dimension.right > 180) dimension.right = 180;
    let source = 'litterati';
    if (action.tagSource) {
        source =
            action.tagSource.toLowerCase() == 'litterati'
                ? 'litterati'
                : 'user';
    }

    const params = {
        challengeIds: arrayOfObjectsToIdsStr(action.selectedChallenges),
        tags: arrayOfObjectsToTitleStr(action.selectedTags),
        userIds: arrayOfObjectsToIdsStr(action.selectedUsers),
        countryCodes: arrayOfObjectsToIdsStr(action.selectedCountries),
        startDate: action.selectedStartDate
            ? new Date(action.selectedStartDate)
            : '',
        endDate: action.selectedEndDate ? new Date(action.selectedEndDate) : '',
        location: action.selectedZipcode,
        radius: action.selectedRadius,
        category: action.category || null,
        object: action.object || null,
        material: action.material || null,
        brand: action.brand || null,
        other: action.other || null,
        zoom: action.zoom || 1,
        source: source,
        ...dimension,
    };
    if (params.startDate) {
        params.startDate = moment(params.startDate).format('yyyy-MM-DD');
    }
    if (params.endDate) {
        params.endDate = moment(params.endDate).format('yyyy-MM-DD');
    }

    try {
        const litters = yield call(getLitters, {
            ...params,
        });
        yield put(
            setMapDataSuccess({
                mapData: litters.data,
            }),
        );
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, params });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(setMapDataFailed(message));
    }
}

export default function*() {
    yield all([
        yield takeLatest(COUNTRIES_AND_CITIES_REQUESTED, fetchPartnerCountries),
        yield takeLatest(CHALLENGES_REQUESTED, fetchChallenges),
        yield takeLatest(TAGS_REQUESTED, fetchTags),
        yield takeLatest(USERS_REQUESTED, fetchUsers),
        yield takeLatest(REPORT_PREVIEW_REQUESTED, fetchReport),
        yield takeLatest(REPORT_CSV_REQUESTED, filterReportLitters),
        yield takeLatest(REPORT_MAP_DATA_REQUESTED, filterReportLitters),
        yield takeLatest(REPORT_CSV_REQUESTED, fetchCSVData),
    ]);
}
